import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * Theo quy ước shadcn/ui: component nằm trong repo, không phải trong
 * `node_modules`. Sửa được trực tiếp mà không phải chống lại API của thư viện —
 * quan trọng với dự án này vì spec mục 13 đòi khá nhiều hành vi riêng
 * (kéo thả, quick action, floating panel).
 */
/**
 * ## Bo 4px và chữ **không đậm** — APM-LIST-b
 *
 * Hai thay đổi nhỏ, và cả hai đến từ việc đo lại một công cụ dữ liệu thật
 * (monday.com): nút của họ bo `4px` và chữ `weight 400`, không phải `500`.
 *
 * Lý do không phải thẩm mỹ thuần tuý. Một thanh công cụ mười nút chữ đậm thì
 * mười nút ấy cùng gào lên, và thứ đáng đọc trên màn hình — dữ liệu — thành
 * phần chìm nhất. Chữ thường ở nút trả lại sức nặng cho nội dung; nút vẫn nổi
 * nhờ nền và viền, hai kênh vốn đã đủ.
 *
 * ## Kiểu kính, bo tròn hết cỡ — người dùng đổi 2026-09-28
 *
 * Mọi nút đi theo chất liệu của thanh điều hướng (liquid glass kiểu iOS 26).
 * Việc này **thay** nửa "bo 4px" của APM-LIST-b ở trên, và thay có ý thức: lý
 * lẽ cũ là một thanh công cụ toàn nút viên thuốc mất cảm giác "điều khiển";
 * người dùng chọn chất kính cho cả ứng dụng. Nửa còn lại **giữ nguyên**: chữ
 * không đậm, vì lý do ấy vẫn đúng — nút đông thì dữ liệu phải là thứ nặng nhất.
 * Ô nhập liệu (`rounded-control`) không đổi: đây chỉ là nút.
 *
 * - `default` — viên màu chính, vệt sáng mép trên (`glassProminent` của iOS).
 * - `outline` — viên kính trắng mờ có vành (`.btn-glass`), hover phủ xám nhạt.
 * - `ghost` — trong suốt, hover là thấu kính xám lõm (`.glass-hover`); có
 *   `aria-pressed`/`aria-expanded` thì giữ thấu kính như mục đang mở.
 * - `secondary` — nền xám nhạt (`tertiarySystemFill`).
 * - `destructive` — viên đỏ, cùng vệt sáng với `default`.
 *
 * Nút co nhẹ khi nhấn (`active:scale`), trừ khi người dùng bật giảm chuyển động —
 * và trừ nút MỞ POPUP (`aria-haspopup`, Radix đặt sẵn khi nút là trigger của menu /
 * popover). Radix mở panel ngay ở `pointerdown`, khoá `pointer-events` của `body`
 * và neo panel vào chính nút đang co: lần bấm ĐẦU nút co rồi bật lại ≈ 250 ms
 * (17 khung hình đổi `scale`, đo 2026-10-01 trên nút "Tài liệu mới"), từ lần hai
 * Chrome không áp `:active` nữa — người dùng thấy "rung lần đầu". Cùng bệnh với
 * `.glass-hover[aria-haspopup]` ở `index.css`.
 */
const PROMINENT_EDGE = 'shadow-[inset_0_1px_0_rgb(255_255_255/0.3),0_1px_2px_rgb(0_0_0/0.15)]';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-normal transition-[color,background-color,box-shadow,scale] duration-150 active:scale-[0.97] motion-reduce:active:scale-100 [&[aria-haspopup]]:active:scale-100 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: `bg-primary text-primary-foreground hover:bg-primary/90 ${PROMINENT_EDGE}`,
        secondary:
          'bg-[rgb(118_118_128/0.12)] text-secondary-foreground hover:bg-[rgb(118_118_128/0.2)]',
        outline: 'btn-glass',
        ghost: 'glass-hover',
        destructive: `bg-destructive text-destructive-foreground hover:bg-destructive/90 ${PROMINENT_EDGE}`,
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3.5 text-xs',
        lg: 'h-11 px-6',
        icon: 'size-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render thành thẻ con (vd `<Link>`) thay vì `<button>`. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
